import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext();
let inFlightCheckAuthPromise = null;
const TENANT_STORAGE_KEY = 'current_tenant_id';

const resolveTenantId = (userPayload) => {
  const available = Array.isArray(userPayload?.available_tenant_ids)
    ? userPayload.available_tenant_ids.filter(Boolean)
    : [];
  const current = userPayload?.current_tenant_id || null;
  const stored = localStorage.getItem(TENANT_STORAGE_KEY);

  if (current && available.includes(current)) {
    return current;
  }
  if (stored && available.includes(stored)) {
    return stored;
  }
  if (available.length > 0) {
    return available[0];
  }
  return null;
};

const isSessionContextValid = (userPayload) => {
  if (!userPayload || typeof userPayload !== 'object') {
    return false;
  }

  const userId = userPayload?.id || userPayload?.user_id || userPayload?.platform_user_id;
  if (!userId) {
    return false;
  }

  const isPlatformAdmin = userPayload?.is_platform_admin === true;
  if (isPlatformAdmin) {
    return true;
  }

  const tenantId = userPayload?.current_tenant_id || null;
  if (!tenantId) {
    return false;
  }

  const available = Array.isArray(userPayload?.available_tenant_ids)
    ? userPayload.available_tenant_ids.filter(Boolean)
    : [];

  if (available.length > 0 && !available.includes(tenantId)) {
    return false;
  }

  return true;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const isMountedRef = useRef(false);

  const checkAuth = useCallback(async () => {
    if (!inFlightCheckAuthPromise) {
      inFlightCheckAuthPromise = authService
        .getCurrentUser()
        .finally(() => {
          inFlightCheckAuthPromise = null;
        });
    }

    if (isMountedRef.current) {
      setLoading(true);
    }

    try {
      let currentUser = await inFlightCheckAuthPromise;
      if (!isMountedRef.current) {
        return;
      }

      if (currentUser) {
        const tenantId = resolveTenantId(currentUser);
        const isPlatformAdmin = currentUser?.is_platform_admin === true;

        if (tenantId) {
          const previousTenantId = localStorage.getItem(TENANT_STORAGE_KEY);
          if (previousTenantId !== tenantId || !currentUser?.current_tenant_id) {
            localStorage.setItem(TENANT_STORAGE_KEY, tenantId);
            const refreshedUser = await authService.getCurrentUser();
            if (refreshedUser) {
              currentUser = refreshedUser;
            }
          }
        } else {
          localStorage.removeItem(TENANT_STORAGE_KEY);
          if (!isPlatformAdmin) {
            authService.logout();
            setIsAuthenticated(false);
            setUser(null);
            return;
          }
        }

        if (!isSessionContextValid(currentUser)) {
          authService.logout();
          localStorage.removeItem(TENANT_STORAGE_KEY);
          setIsAuthenticated(false);
          setUser(null);
          return;
        }

        setUser(currentUser);
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }
      const status = Number(error?.response?.status);
      if (status === 401) {
        authService.logout();
        localStorage.removeItem(TENANT_STORAGE_KEY);
        setIsAuthenticated(false);
        setUser(null);
      } else {
        // Keep the last authenticated snapshot on transient failures (network/timeouts/backend hiccups).
        console.error('[AuthContext] checkAuth transient error', error);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    checkAuth();
    return () => {
      isMountedRef.current = false;
    };
  }, [checkAuth]);

  const login = async (email, senha, options = {}) => {
    const tenantId = String(options?.tenantId || '').trim();
    if (tenantId) {
      localStorage.setItem(TENANT_STORAGE_KEY, tenantId);
      localStorage.removeItem('tenant_id');
      localStorage.removeItem('selected_tenant_id');
    }

    const data = await authService.login(email, senha, { tenantId: tenantId || undefined });
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);
    await checkAuth();
  };

  const logout = () => {
    authService.logout();
    localStorage.removeItem(TENANT_STORAGE_KEY);
    setUser(null);
    setIsAuthenticated(false);
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
};
